import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { METER_ROLE_ICONS, MeterRole, MeterRoleOrigin } from '@core/models';
import { InstallationAttributesService } from '@core/services/installation-attributes.service';
import {
  AlertBannerComponent,
  DialogShellComponent,
  FilledButtonComponent,
  TextButtonComponent,
} from '@custom-components';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsRefusal, settingsRefusal } from '../../settings-refusal';

export interface MeterRoleDialogData {
  deviceId: number;
  ldn: string;
  current: MeterRole;
  origin: MeterRoleOrigin;
  // Other installations the device feeds, whose pages show the new role too. Null when they could
  // not be read.
  otherInstallations: number | null;
}

export const METER_ROLES: MeterRole[] = ['consumption', 'production', 'bidirectional'];

// Closes with the role as written, or with nothing when cancelled.
@Component({
  selector: 'xemex-meter-role-dialog',
  imports: [
    AlertBannerComponent,
    DialogShellComponent,
    FilledButtonComponent,
    MatIconModule,
    MatRadioModule,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './meter-role-dialog.component.html',
})
export class MeterRoleDialogComponent {
  readonly data = inject<MeterRoleDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<MeterRoleDialogComponent, MeterRole>>(MatDialogRef);
  private readonly service = inject(InstallationAttributesService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly roles = METER_ROLES;
  protected readonly icons = METER_ROLE_ICONS;

  readonly selected = signal<MeterRole>(this.data.current);
  readonly isSaving = signal(false);
  readonly refusal = signal<SettingsRefusal | null>(null);
  // Read by the opener once the dialog closes: the device changed underneath.
  readonly stale = computed(() => this.refusal()?.stale ?? false);
  readonly canSave = computed(
    () => this.selected() !== this.data.current && !this.isSaving() && !this.stale(),
  );

  select(role: MeterRole): void {
    this.selected.set(role);
    this.refusal.set(null);
  }

  save(): void {
    if (!this.canSave()) return;
    const role = this.selected();
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .setMeterRole(this.data.deviceId, role)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close(role),
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.refusal.set(
            settingsRefusal(toRefusal(error), 'installations.attributes.errors.notDlms'),
          );
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
